import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Profile {
  icon: string;
  color: string;
  title: string;
  desc: string;
}

const ConsultingWhoFor = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  const profiles: Profile[] = [
    {
      icon: 'ri-user-star-line',
      color: '#8c52ff',
      title: t('consulting.whoFor.items.p1.title'),
      desc: t('consulting.whoFor.items.p1.desc'),
    },
    {
      icon: 'ri-store-2-line',
      color: '#00c2ff',
      title: t('consulting.whoFor.items.p2.title'),
      desc: t('consulting.whoFor.items.p2.desc'),
    },
    {
      icon: 'ri-briefcase-line',
      color: '#FF2DAA',
      title: t('consulting.whoFor.items.p3.title'),
      desc: t('consulting.whoFor.items.p3.desc'),
    },
    {
      icon: 'ri-rocket-line',
      color: '#8c52ff',
      title: t('consulting.whoFor.items.p4.title'),
      desc: t('consulting.whoFor.items.p4.desc'),
    },
    {
      icon: 'ri-global-line',
      color: '#00c2ff',
      title: t('consulting.whoFor.items.p5.title'),
      desc: t('consulting.whoFor.items.p5.desc'),
    },
    {
      icon: 'ri-map-pin-2-line',
      color: '#FF2DAA',
      title: t('consulting.whoFor.items.p6.title'),
      desc: t('consulting.whoFor.items.p6.desc'),
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative py-24 bg-[#050008] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(140,82,255,0.08)_0%,transparent_60%)]" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 bg-[#FF2DAA]/10 border border-[#FF2DAA]/30 rounded-full px-4 py-1.5 text-[#FF2DAA] text-xs font-bold uppercase tracking-widest mb-5">
            <i className="ri-user-heart-line" />
            {t('consulting.whoFor.hero.badge')}
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            {t('consulting.whoFor.hero.titlePrefix')} <span className="bg-gradient-to-r from-[#FF2DAA] to-[#8c52ff] bg-clip-text text-transparent">{t('consulting.whoFor.hero.titleHighlight')}</span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            {t('consulting.whoFor.hero.subtitle')}
          </p>
        </div>

        <div
          ref={ref}
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {profiles.map((p, i) => (
            <div
              key={p.title}
              className="relative rounded-2xl p-6 border border-white/8 transition-all duration-300 hover:border-white/20 group"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                transitionDelay: `${i * 60}ms`,
              }}
            >
              <div
                className="w-12 h-12 flex items-center justify-center rounded-xl mb-4"
                style={{ background: `${p.color}15`, border: `1px solid ${p.color}30` }}
              >
                <i className={`${p.icon} text-xl`} style={{ color: p.color }} />
              </div>
              <h3 className="text-base font-bold text-white mb-2">{p.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{p.desc}</p>
              <div
                className="absolute bottom-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: `linear-gradient(90deg, transparent, ${p.color}60, transparent)` }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ConsultingWhoFor;
